import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onClose", "onInstallComplete", "onInstallError", "buttons"],
  _excluded2 = ["onClick"],
  _excluded3 = ["onClick"];
import { MagpieConnection } from 'magpie-lib/connection';
import { marketplaceModalComponentDef } from 'navigation-components/config';
import { useNavigationComponent } from 'nest/application/hooks';
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import * as t from 'ts-schema';
import { ButtonClicked, CloseModal, InstallComplete, InstallError, MarketplaceModalIFrameTopic, publishCloseModal } from '../components/internal/MarketplaceModalIFrame';
import { unsubscribeAll } from '../utils/unsubscribeAll';
export const marketplaceModalTypeSchema = t.union(t.literal('LISTING'), t.literal('EXPLORE'), t.literal('SUGGESTION'), t.literal('COLLECTION'), t.literal('INSTALL'));
export function useMarketplaceModal(_ref) {
  let {
      onClose,
      onInstallComplete,
      onInstallError,
      buttons
    } = _ref,
    passthruProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const lastClosedTime = useRef(new Date().getTime());
  const modalInstanceId = useId();
  const navComponent = useNavigationComponent({
    componentDef: marketplaceModalComponentDef
  });
  const buttonsWithKeys = useMemo(() => {
    var _buttons$primary, _buttons$secondary;
    return {
      primary: buttons === null || buttons === void 0 || (_buttons$primary = buttons.primary) === null || _buttons$primary === void 0 ? void 0 : _buttons$primary.map((b, i) => Object.assign({}, b, {
        key: `primary-${i}`
      })),
      secondary: buttons === null || buttons === void 0 || (_buttons$secondary = buttons.secondary) === null || _buttons$secondary === void 0 ? void 0 : _buttons$secondary.map((b, i) => Object.assign({}, b, {
        key: `secondary-${i}`
      }))
    };
  }, [buttons]);

  // begin preloading after a 1 sec delay (to allow the connection to establish)
  useEffect(() => {
    setTimeout(async () => {
      const componentState = await navComponent.getState();

      // don't call hide() if the user was fast and already manually opened it
      if (componentState.isOpen) return;

      // hide will actually load the component
      await navComponent.hide();
    }, 1000);
  }, [navComponent]);

  // subscribe to modal events
  useEffect(() => {
    const topic = MagpieConnection.get().forTopic(MarketplaceModalIFrameTopic);
    return unsubscribeAll(topic.on(CloseModal.getType(), () => {
      lastClosedTime.current = new Date().getTime();
      onClose === null || onClose === void 0 || onClose();
    }, {
      filter: ({
        payload
      }) => payload.modalInstanceId === undefined || payload.modalInstanceId === modalInstanceId
    }), topic.on(ButtonClicked.getType(), ({
      payload
    }) => {
      var _buttonsWithKeys$prim, _buttonsWithKeys$seco, _matchingButton$onCli;
      const combinedButtons = [...((_buttonsWithKeys$prim = buttonsWithKeys === null || buttonsWithKeys === void 0 ? void 0 : buttonsWithKeys.primary) !== null && _buttonsWithKeys$prim !== void 0 ? _buttonsWithKeys$prim : []), ...((_buttonsWithKeys$seco = buttonsWithKeys === null || buttonsWithKeys === void 0 ? void 0 : buttonsWithKeys.secondary) !== null && _buttonsWithKeys$seco !== void 0 ? _buttonsWithKeys$seco : [])];
      const matchingButton = combinedButtons === null || combinedButtons === void 0 ? void 0 : combinedButtons.find(c => c.key === payload.buttonKey);
      matchingButton === null || matchingButton === void 0 || (_matchingButton$onCli = matchingButton.onClick) === null || _matchingButton$onCli === void 0 || _matchingButton$onCli.call(matchingButton, {
        close: () => publishCloseModal(modalInstanceId),
        listingId: payload.listingId,
        slug: payload.slug,
        sourceId: payload.sourceId
      });
    }, {
      filter: ({
        payload
      }) => payload.modalInstanceId === undefined || payload.modalInstanceId === modalInstanceId
    }), topic.on(InstallComplete.getType(), () => onInstallComplete === null || onInstallComplete === void 0 ? void 0 : onInstallComplete(), {
      filter: ({
        payload
      }) => payload.modalInstanceId === undefined || payload.modalInstanceId === modalInstanceId
    }), topic.on(InstallError.getType(), () => onInstallError === null || onInstallError === void 0 ? void 0 : onInstallError(), {
      filter: ({
        payload
      }) => payload.modalInstanceId === undefined || payload.modalInstanceId === modalInstanceId
    }));
  }, [buttonsWithKeys, modalInstanceId, onClose, onInstallComplete, onInstallError]);
  const open = useCallback(dynamicPassthruProps => {
    var _buttonsWithKeys$prim2, _buttonsWithKeys$seco2, _dynamicPassthruProps;
    // attempting to open the modal while it's still closing will make it become unresponsive,
    // so this is a bit of a hack to prevent speedy users from breaking things.
    if (new Date().getTime() - lastClosedTime.current < 500) return;
    const finalPassthruProps = Object.assign({}, passthruProps, dynamicPassthruProps, {
      modalInstanceId,
      // strip out the `onClick` properties so they don't make a console error when put in passthruProps
      buttons: {
        primary: buttonsWithKeys === null || buttonsWithKeys === void 0 || (_buttonsWithKeys$prim2 = buttonsWithKeys.primary) === null || _buttonsWithKeys$prim2 === void 0 ? void 0 : _buttonsWithKeys$prim2.map(b => {
          const rest = _objectWithoutPropertiesLoose(b, _excluded2);
          return rest;
        }),
        secondary: buttonsWithKeys === null || buttonsWithKeys === void 0 || (_buttonsWithKeys$seco2 = buttonsWithKeys.secondary) === null || _buttonsWithKeys$seco2 === void 0 ? void 0 : _buttonsWithKeys$seco2.map(b => {
          const rest = _objectWithoutPropertiesLoose(b, _excluded3);
          return rest;
        })
      }
    });
    navComponent.setProps({
      passthruProps: Object.assign({}, finalPassthruProps, {
        // TODO: maybe delete all this `hideBackButton` and instead
        //       whether to show it based on the history stack
        hideBackButton: (_dynamicPassthruProps = dynamicPassthruProps === null || dynamicPassthruProps === void 0 ? void 0 : dynamicPassthruProps.hideBackButton) !== null && _dynamicPassthruProps !== void 0 ? _dynamicPassthruProps : finalPassthruProps.modalType === 'LISTING'
      })
    }).then(() => navComponent.show().catch(console.error)).catch(console.error);
  }, [buttonsWithKeys, modalInstanceId, navComponent, passthruProps]);
  return {
    open,
    close: () => publishCloseModal(modalInstanceId)
  };
}